import { FC } from 'react';
// Types
import PageTypes from 'types/PageTypes';
// Forms (ready-to-use form sections extracted from QuizFormPage)
import WaiverForm from 'pages/OwnerPage/QuizFormPage/TabContentPages/SystemPages/Waiver/WaiverForm';
import CountdownForm from 'pages/OwnerPage/QuizFormPage/TabContentPages/SystemPages/Countdown/CountdownForm';
import CallToActionForm from 'pages/OwnerPage/QuizFormPage/TabContentPages/SystemPages/CallToAction/CallToActionForm';
import ResultsForm from 'pages/OwnerPage/QuizFormPage/TabContentPages/SystemPages/Results/ResultsForm';
import RegistrationForm from 'pages/OwnerPage/QuizFormPage/TabContentPages/SystemPages/Registration/RegistrationForm';
// Dynamic page forms
import LandingPageForm from './forms/LandingPageForm';
import QuestionPageForm from './forms/QuestionPageForm';
import BannerPageForm from './forms/BannerPageForm';
import TimerPageForm from './forms/TimerPageForm';
import WaitingPageForm from './forms/WaitingPageForm';
import AdditionalQuestionsPageForm from './forms/AdditionalQuestionsPageForm';

/**
 * Props every per-page form may receive. Forms that don't care about a
 * specific entry (LandingPage, Waiver, Registration, Countdown, …) can simply
 * declare `FC` and ignore `pageRef`; forms editing one-of-many entries
 * (Question, Banner, Timer, Waiting, AdditionalQuestions) should read it.
 */
export type PageEditFormProps = {
  pageRef?: string;
  quizId?: string;
};

export type PageEditFormComponent = FC<PageEditFormProps>;

type PageEditEntry = {
  /** Title shown in the dialog header. Plain string to match the labels used
   *  in QuizAgentChatScreen's screens list. Swap to i18n keys once we agree on
   *  a translations namespace for the agent dialog. */
  label: string;
  /** The form to render inside the dialog. `null` = page is not editable yet:
   *  the edit button will be hidden on the screen card and the dialog falls
   *  back to a "coming soon" placeholder if somehow opened. */
  Form: PageEditFormComponent | null;
};

/**
 * Single source of truth for per-page edit support inside QuizAgentDialog.
 *
 * Keys are the same identifiers QuizAgentChatScreen uses for its preview
 * screens (`'start' | 'waiver' | 'register' | 'countdown' | 'post-quiz' |
 * 'results' | 'leaderboard'`) and the dynamic PageTypes values from
 * `quiz.pages[].type` (`'question' | 'banner' | …`).
 */
const REGISTRY: Record<string, PageEditEntry> = {
  start:       { label: 'Landing Page', Form: LandingPageForm },
  waiver:      { label: 'Waiver',       Form: WaiverForm },
  register:    { label: 'Registration', Form: RegistrationForm },
  countdown:   { label: 'Countdown',    Form: CountdownForm },
  'post-quiz': { label: 'Post Quiz',    Form: CallToActionForm },
  results:     { label: 'Results',      Form: ResultsForm },
  leaderboard: { label: 'Leaderboard',  Form: ResultsForm },

  // Dynamic pages from `quiz.pages[]`.
  [PageTypes.Question]:            { label: 'Question',             Form: QuestionPageForm },
  [PageTypes.AdditionalQuestions]: { label: 'Additional Questions', Form: AdditionalQuestionsPageForm },
  [PageTypes.Banner]:              { label: 'Banner',               Form: BannerPageForm },
  [PageTypes.Timer]:               { label: 'Timer',                Form: TimerPageForm },
  [PageTypes.Waiting]:             { label: 'Waiting',              Form: WaitingPageForm },
};

export const getQuizPageEditEntry = (page: string): PageEditEntry | null =>
  REGISTRY[page] ?? null;

export const isQuizPageEditable = (page: string): boolean =>
  !!REGISTRY[page]?.Form;

export const getQuizPageLabel = (page: string): string =>
  REGISTRY[page]?.label ?? page;
